import { useState } from 'react';
import { useRecoilCallback } from 'recoil';
import { useMutation } from '@apollo/client';

import Dialog from 'components/common/Dialog/Dialog';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { teamSwitchUrlState } from 'components/common/Managers/RouteStateManager.state';

import { SET_LINK_MUTATION } from 'components/linkAccount/queries';

import './TeamSwitchDialog.scss';

/**
 * Returns a function that reloads the current page for a team switch
 */
export const useTeamSwitchReload = (): (() => void) =>
  // Need to full reload the page to request resources from the proper team.
  // TODO: Once butter message exists, or our standard for displaying errors, handle error state.
  // if mutation does not fire succesfully, this will all fail silently
  useRecoilCallback(({ snapshot }) => (): void => {
    window.location.href = snapshot.getLoadable(teamSwitchUrlState).getValue();
  });

/**
 * TeamSwitchDialog is a component that composes the Dialog component in order to handle switching to a new team within scalyr.
 *
 * Required
 * @param   {Boolean}   showModal     Initial open/close state of modal.
 * @param   {String}    message       Message for the dialog to display to the User.
 * @param   {Object}    actual        Object containing email and token properties for the current team the user is linked to.
 *
 * Optional
 * @param   {Object}    requested     Object containing the email and token properties for requested team. May be null
 *
 * @returns {Component}
 */

type Props = {
  showModal: boolean;
  message: string;
  // TODO: Need to type TEAM_TOKEN_QUERY response
  // eslint-disable-next-line
  actual: any;
  // eslint-disable-next-line
  requested?: any;
};

function TeamSwitchDialog({ showModal, message, actual, requested }: Props): JSX.Element {
  const [show, setShow] = useState(showModal);
  const teamSwitchReload = useTeamSwitchReload();
  const [switchTeams, { data }] = useMutation(SET_LINK_MUTATION);

  if (data && data.switchTeams) {
    teamSwitchReload();
  }

  /**
   * Handles canceling the request to switch teams. We redirect the user to the base page
   * (no extra query string params other than 'mode') that they were trying to navigate to.
   */
  const cancelSwitch = (): void => {
    teamSwitchReload();
  };

  /**
   * Returns a mutation function with the appropriate email passed as the targetAccountEmail variable.
   */
  const handleSwitch = (): void => {
    switchTeams({ variables: { targetAccountEmail: requested.email } });
  };

  /**
   * Custom footer for the Dialog, used when there is no valid requested team to switch to.
   * Only gives one option for the dialog, which is to cancel and load the appropriate page/data for the current team.
   */
  const footerProps = {
    items: [
      <Button variant={ButtonVariant.PRIMARY} onClick={cancelSwitch}>
        Ok
      </Button>,
    ],
  };

  let secondaryMessage = (
    <div className="TeamSwitchDialog__message">You will be redirected back to {actual.email}.</div>
  );

  if (requested) {
    secondaryMessage = (
      <div className="TeamSwitchDialog__message">Would you like to switch teams to {requested.email}?</div>
    );
  }

  return (
    <Dialog
      title={requested ? 'Switch Teams' : 'Invalid Team'}
      show={show}
      close={(): void => setShow(false)}
      cancelCallback={cancelSwitch}
      okCallback={requested ? handleSwitch : undefined}
      footer={requested ? undefined : footerProps}
    >
      <div className="TeamSwitchDialog__message">{message}</div>
      {secondaryMessage}
    </Dialog>
  );
}

export default TeamSwitchDialog;
